import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, Users, MagnifyingGlass, Plus } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const CreateGroupPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [groupName, setGroupName] = useState('');
  const [groupDescription, setGroupDescription] = useState('');
  const [staffList, setStaffList] = useState([]);
  const [clientList, setClientList] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('clients');

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    fetchAllMembers();
  }, [user, navigate]);

  const fetchAllMembers = async () => {
    try {
      const [customerRes, staffRes] = await Promise.all([
        axios.get(`${API}/api/users/all-customers`),
        axios.get(`${API}/api/users/all-staff`)
      ]);
      setClientList(customerRes.data || []);
      setStaffList(staffRes.data || []);
    } catch (error) {
      console.error('Failed to fetch members:', error);
      toast.error('Failed to load members');
    }
  };

  const toggleMember = (memberId) => {
    if (selectedMembers.includes(memberId)) {
      setSelectedMembers(selectedMembers.filter(id => id !== memberId));
    } else {
      setSelectedMembers([...selectedMembers, memberId]);
    }
  };

  const createGroup = async () => {
    if (!groupName.trim()) {
      toast.error('Please enter group name');
      return;
    }

    if (selectedMembers.length === 0) {
      toast.error('Please select at least one member');
      return;
    }

    setLoading(true);

    try {
      const hasClients = selectedMembers.some(id => clientList.find(c => c.id === id));
      const groupType = hasClients ? 'client_group' : 'staff_group';

      const response = await axios.post(`${API}/api/groups/create`, {
        name: groupName,
        description: groupDescription,
        group_type: groupType,
        created_by: user.id,
        member_ids: selectedMembers
      });

      toast.success('Group created successfully!');
      navigate(`/groups/${response.data.group_id}`);
    } catch (error) {
      console.error('Failed to create group:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to create group';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const currentList = activeTab === 'clients' ? clientList : staffList;
  const filteredMembers = currentList.filter(member =>
    member.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    member.phone?.includes(searchQuery)
  );

  const selectedStaffCount = selectedMembers.filter(id => staffList.find(s => s.id === id)).length;
  const selectedClientCount = selectedMembers.filter(id => clientList.find(c => c.id === id)).length;

  const handleSelectAllInTab = () => {
    const allIds = filteredMembers.map(m => m.id);
    const allSelected = allIds.every(id => selectedMembers.includes(id));
    if (allSelected) {
      setSelectedMembers(selectedMembers.filter(id => !allIds.includes(id)));
    } else {
      const newSelected = [...new Set([...selectedMembers, ...allIds])];
      setSelectedMembers(newSelected);
    }
  };

  return (
    <div className="h-screen flex flex-col bg-wa-hover">
      {/* Header */}
      <div className="bg-wa-primary-strong text-white p-4 flex-shrink-0">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="hover:bg-green-700 p-2 rounded-full" data-testid="back-btn">
            <ArrowLeft size={24} weight="bold" />
          </button>
          <h1 className="text-xl font-bold">Create New Group</h1>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto" style={{ paddingBottom: '80px', WebkitOverflowScrolling: 'touch' }}>
        <div className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Group Info */}
        <div className="bg-wa-panel rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Group Details</h2>

          {/* Group Name */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-wa-text-primary mb-2">
              Group Name *
            </label>
            <input
              type="text"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="Enter group name"
              data-testid="group-name-input"
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>

          {/* Group Description */}
          <div>
            <label className="block text-sm font-medium text-wa-text-primary mb-2">
              Description (Optional)
            </label>
            <textarea
              value={groupDescription}
              onChange={(e) => setGroupDescription(e.target.value)}
              placeholder="Enter group description"
              rows="3"
              data-testid="group-desc-input"
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
            />
          </div>
        </div>

        {/* Member Selection - Unified with Tabs */}
        <div className="bg-wa-panel rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Add Members</h2>
            <button
              onClick={handleSelectAllInTab}
              className="text-sm text-green-600 hover:text-wa-text-primary font-medium"
              data-testid="select-all-btn"
            >
              Select All
            </button>
          </div>

          {/* Tabs: Clients / Staff */}
          <div className="flex border-b mb-4" data-testid="member-tabs">
            <button
              onClick={() => setActiveTab('clients')}
              className={`flex-1 py-2.5 text-center font-medium text-sm transition-colors ${
                activeTab === 'clients'
                  ? 'text-green-600 border-b-2 border-green-600'
                  : 'text-wa-text-secondary hover:text-wa-text-primary'
              }`}
              data-testid="tab-clients"
            >
              Clients {selectedClientCount > 0 && <span className="ml-1 bg-wa-hover text-wa-text-primary px-1.5 py-0.5 rounded-full text-xs">{selectedClientCount}</span>}
            </button>
            <button
              onClick={() => setActiveTab('staff')}
              className={`flex-1 py-2.5 text-center font-medium text-sm transition-colors ${
                activeTab === 'staff'
                  ? 'text-green-600 border-b-2 border-green-600'
                  : 'text-wa-text-secondary hover:text-wa-text-primary'
              }`}
              data-testid="tab-staff"
            >
              Staff {selectedStaffCount > 0 && <span className="ml-1 bg-wa-hover text-wa-text-primary px-1.5 py-0.5 rounded-full text-xs">{selectedStaffCount}</span>}
            </button>
          </div>

          {/* Search */}
          <div className="relative mb-4">
            <MagnifyingGlass
              size={20}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-wa-text-secondary"
            />
            <input
              type="text"
              placeholder={activeTab === 'clients' ? 'Search clients...' : 'Search staff...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="search-members-input"
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>

          {/* Selected Count */}
          <div className="mb-4 text-sm text-wa-text-secondary" data-testid="selected-count">
            {selectedMembers.length} member(s) selected
            {selectedClientCount > 0 && selectedStaffCount > 0 && (
              <span className="ml-2 text-xs text-green-600 font-medium">(Mixed Group)</span>
            )}
          </div>

          {/* Members List */}
          <div className="overflow-y-auto border rounded-lg" data-testid="members-list">
            {filteredMembers.length === 0 ? (
              <div className="text-center py-8 text-wa-text-secondary">
                No {activeTab === 'clients' ? 'clients' : 'staff'} found
              </div>
            ) : (
              filteredMembers.map(member => (
                <label
                  key={member.id}
                  className="flex items-center p-3 hover:bg-wa-hover cursor-pointer border-b last:border-b-0"
                  data-testid={`member-${member.id}`}
                >
                  <input
                    type="checkbox"
                    checked={selectedMembers.includes(member.id)}
                    onChange={() => toggleMember(member.id)}
                    className="mr-3 w-4 h-4 accent-green-600"
                  />
                  <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center mr-3 flex-shrink-0">
                    <span className="text-wa-text-secondary font-semibold text-sm">
                      {(member.username || '?')[0].toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{member.username}</div>
                    <div className="text-sm text-wa-text-secondary truncate">
                      {member.phone}
                      {member.role && (
                        <span className={`ml-2 text-xs px-2 py-0.5 rounded ${
                          activeTab === 'staff' ? 'bg-wa-hover text-wa-text-primary' : 'bg-gray-200 text-wa-text-secondary'
                        }`}>
                          {member.role.replace('_', ' ')}
                        </span>
                      )}
                    </div>
                  </div>
                </label>
              ))
            )}
          </div>
        </div>
      </div>
      </div>

      {/* Sticky Create Button at Bottom */}
      <div className="fixed bottom-0 left-0 right-0 bg-wa-panel border-t border-wa-border p-4 shadow-lg z-50">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={createGroup}
            disabled={loading || !groupName.trim() || selectedMembers.length === 0}
            data-testid="create-group-btn"
            className="w-full bg-wa-primary-strong text-white py-3 rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-400 flex items-center justify-center gap-2 transition-colors"
          >
            <Plus size={20} weight="bold" />
            {loading ? 'Creating Group...' : `Create Group (${selectedMembers.length} members)`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateGroupPage;
